import styled, { css } from 'styled-components'

export const FavoritesShell = styled.div`
  width: 100%;
  padding: 36px clamp(18px, 3vw, 36px) 56px;
  margin-top: 80px;
`

export const WorkspaceFrame = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 24px;
  min-height: calc(100vh - 220px);

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

const panelSurface = css`
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, ${p => p.theme.palette.colors.shellGradientStart} 0%, transparent 42%),
    ${p => p.theme.palette.colors.surfaceElevatedSoft};
  box-shadow:
    0 20px 54px ${p => p.theme.palette.colors.shadowStrong},
    inset 0 1px 0 ${p => p.theme.palette.colors.borderSoft};
  backdrop-filter: blur(18px);
`

export const MainPanel = styled.section`
  ${panelSurface};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
`

export const SectionEyebrow = styled.div`
  font-family: Poppins;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${p => p.theme.palette.colors.subdued};
`

export const MainHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  padding-bottom: 18px;
  border-bottom: 1px solid ${p => p.theme.palette.colors.borderSoft};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

export const MainHeadingBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const MainTitle = styled.div`
  font-family: Gilroy;
  font-size: clamp(28px, 3vw, 38px);
  font-weight: 700;
  line-height: 1;
  color: ${p => p.theme.palette.colors.text};
`

export const MainSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${p => p.theme.palette.colors.muted};
`

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

export const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

export const SelectSlot = styled.div`
  min-width: 220px;
`

export const SelectionPill = styled.div`
  min-height: 42px;
  border-radius: 999px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: ${p => p.theme.palette.colors.badgeMuted};
  color: ${p => p.theme.palette.colors.text};
  font-size: 13px;
  font-weight: 700;
`

export const ViewSwitch = styled.div`
  display: flex;
  padding: 4px;
  border-radius: 999px;
  background: ${p => p.theme.palette.colors.surfaceElevatedStrong};
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
`

export const ViewButton = styled.button`
  min-width: 84px;
  height: 38px;
  border-radius: 999px;
  border: 0;
  background: ${p => p.active ? p.theme.palette.colors.badgeMuted : 'transparent'};
  color: ${p => p.theme.palette.colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
`

export const ContentGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: start;
`

export const ContentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`

export const EmptyState = styled.div`
  min-height: 320px;
  border: 1px dashed ${p => p.theme.palette.colors.borderSoft};
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  padding: 32px;
  background: ${p => p.theme.palette.colors.surfaceElevated};
`

export const EmptyTitle = styled.div`
  font-family: Gilroy;
  font-size: 28px;
  font-weight: 700;
  color: ${p => p.theme.palette.colors.text};
`

export const EmptyText = styled.p`
  margin: 0;
  max-width: 360px;
  font-size: 14px;
  line-height: 1.6;
  color: ${p => p.theme.palette.colors.muted};
`

export const DragGhostStack = styled.div`
  position: relative;
  width: min(320px, 76vw);
  pointer-events: none;
`

export const DragTrail = styled.div`
  position: absolute;
  inset: 10px 18px auto auto;
  width: calc(100% - 28px);
  height: 82px;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 22px;
  background: ${p => p.theme.palette.colors.surfaceElevatedSoft};
  box-shadow: 0 18px 42px ${p => p.theme.palette.colors.shadowStrong};
  opacity: 0.42;
  transform: rotate(-5deg) translate(-18px, 10px);

  &:first-child {
    opacity: 0.24;
    transform: rotate(5deg) translate(-34px, 22px);
  }
`

export const DragGhostCard = styled.div`
  position: relative;
  min-height: 92px;
  border: 1px solid ${p => p.theme.palette.blue.main};
  border-radius: 24px;
  padding: 14px 54px 14px 14px;
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  align-items: center;
  gap: 14px;
  background:
    radial-gradient(circle at 10% 0%, ${p => p.theme.palette.colors.shellGlow} 0%, transparent 46%),
    ${p => p.theme.palette.colors.surfaceElevated};
  color: ${p => p.theme.palette.colors.text};
  box-shadow:
    0 26px 72px ${p => p.theme.palette.colors.shadowLift},
    inset 0 1px 0 ${p => p.theme.palette.colors.borderSoft};
`

export const DragGhostThumb = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  object-fit: cover;
  background: ${p => p.theme.palette.colors.surface};
  box-shadow: 0 14px 30px ${p => p.theme.palette.colors.shadowStrong};
`

export const DragGhostTitle = styled.div`
  min-width: 0;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  font-family: Gilroy;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.12;
`

export const DragGhostBadge = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  min-width: 34px;
  height: 34px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.palette.blue.main};
  color: ${p => p.theme.palette.blue.contrastText};
  font-size: 13px;
  font-weight: 800;
  box-shadow: 0 12px 28px ${p => p.theme.palette.colors.shadowStrong};
`
